import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { FaCheckCircle, FaTimesCircle, FaQrcode, FaUserCheck, FaCamera } from 'react-icons/fa';
import jsQR from 'jsqr';

// Polyfill for BarcodeDetector
const loadBarcodeDetector = async () => {
  // If BarcodeDetector is already available, use it
  if ('BarcodeDetector' in window) {
    return window.BarcodeDetector;
  }
  
  try {
    // Try to use the native implementation
    const detector = new (window.BarcodeDetector || window['BarcodeDetector'])();
    if (detector) return window.BarcodeDetector;
  } catch (e) {
    console.log('Native BarcodeDetector not available, using polyfill');
  }
  
  // Fallback to a simple implementation that shows a file upload option
  class BarcodeDetectorPolyfill {
    constructor(options) {
      this.options = options || { formats: ['qr_code'] };
    }
    
    static async getSupportedFormats() {
      return ['qr_code'];
    }
    
    async detect(image) {
      // This is a no-op for the polyfill
      return [];
    }
  }
  
  window.BarcodeDetector = BarcodeDetectorPolyfill;
  return BarcodeDetectorPolyfill;
};

// Initialize the detector when the component loads
let BarcodeDetector = {
  isPolyfill: true,
  async detect() { return []; },
  async getSupportedFormats() { return []; }
};

// Load the appropriate detector
let detectorLoaded = false;
loadBarcodeDetector()
  .then(detector => {
    BarcodeDetector = detector;
    detectorLoaded = true;
  })
  .catch(err => {
    console.error('Failed to load BarcodeDetector:', err);
  });

const QRScanner = ({ onScanComplete, disabled = false }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const { showToast, showError, showSuccess } = useToast();
  const [memberData, setMemberData] = useState(null);
  const [isScanning, setIsScanning] = useState(true);
  const [useFileUpload, setUseFileUpload] = useState(false);
  const [filePreview, setFilePreview] = useState(null);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const animationFrameRef = useRef(null);

  // QR code scanning loop
  const scanLoop = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    
    // Set canvas size to match video size
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    try {
      // Get image data and process for QR code
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      
      // Process the image data with jsQR
      const code = jsQR(
        imageData.data,
        imageData.width,
        imageData.height,
        {
          inversionAttempts: 'dontInvert',
        }
      );
      
      if (code) {
        // QR code found!
        // console.log('QR Code detected:', code.data);
        handleScan(code.data);
        return;
      }
    } catch (err) {
      console.error('Error processing QR code:', err);
    }
    
    // Continue the loop
    if (isScanning) {
      animationFrameRef.current = requestAnimationFrame(scanLoop);
    }
  };

  const [availableCameras, setAvailableCameras] = useState([]);
  const [activeCamera, setActiveCamera] = useState(null);

  // Get all available cameras
  const getAvailableCameras = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ video: true }); // Request camera access first
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      setAvailableCameras(videoDevices);
      return videoDevices;
    } catch (err) {
      console.error('Error getting camera devices:', err);
      showError('Could not access camera devices. Please check permissions.');
      return [];
    }
  };

  const startCamera = async (deviceId = null) => {
    try {
      setLoading(true);
      const constraints = {
        video: {
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          frameRate: { ideal: 30 },
          ...(deviceId ? { deviceId: { exact: deviceId } } : { facingMode: 'environment' })
        },
        audio: false
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      // Stop any existing stream
      if (streamRef.current) {
        stopCamera();
      }
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        
        // Set up video element to fill container
        videoRef.current.onloadedmetadata = () => {
          const video = videoRef.current;
          video.style.width = '100%';
          video.style.height = '100%';
          video.style.objectFit = 'cover';
          
          // Start the scanning loop
          scanLoop();
        };
      }
      
      // Update active camera
      if (deviceId) {
        const cameras = await getAvailableCameras();
        const active = cameras.find(cam => cam.deviceId === deviceId);
        setActiveCamera(active || null);
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      showError('Could not access the camera. Please check permissions.');
      setUseFileUpload(true);
    } finally {
      setLoading(false);
    }
  };

  // Initialize camera when component mounts and when isScanning changes
  useEffect(() => {
    if (disabled) {
      stopCamera();
      setIsScanning(false);
      return;
    }
    
    if (!isScanning) {
      stopCamera();
      return;
    }
    
    let isMounted = true;
    
    const initScanner = async () => {
      try {
        // Get available cameras
        const cameras = await getAvailableCameras();
        
        if (cameras.length === 0) {
          throw new Error('No cameras found');
        }
        
        // Start with the first available camera
        if (isMounted) {
          await startCamera(cameras[0].deviceId);
        }
        
      } catch (err) {
        console.error('Camera initialization error:', err);
        if (isMounted) {
          showError(`Failed to access camera: ${err.message}`);
          setUseFileUpload(true);
        }
      }
    };
    
    initScanner();
    
    return () => {
      isMounted = false;
      stopCamera();
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isScanning, disabled]);
  
  // Handle camera switch
  const switchCamera = async (deviceId) => {
    if (deviceId === activeCamera?.deviceId) return;
    await startCamera(deviceId);
  }; 

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  // Start/stop camera based on isScanning state
  useEffect(() => {
    if (isScanning && !disabled) {
      startCamera();
    } else {
      stopCamera();
    }
    
    return () => {
      stopCamera();
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isScanning, disabled]);

  const startQRScanning = () => {
    if (!videoRef.current || !canvasRef.current || disabled || !isScanning) {
      return;
    }
    
    // Start the scanning loop
    scanLoop();
    
    // Return cleanup function
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  };

  const handleScan = async (scannedData) => {
    if (!scannedData || !isScanning) return;
    
    // Prevent multiple scans of the same code
    if (result === scannedData) return;
    
    setLoading(true);
    setIsScanning(false);
    setResult(scannedData);
    
    try {
      // console.log('Scanned data:', scannedData);
      
      // Parse the QR code data (could be JSON or just a string)
      let qrData;
      try {
        qrData = JSON.parse(scannedData);
      } catch (e) {
        // If not JSON, treat as plain text (SLIITID)
        qrData = { SLIITID: scannedData };
      }
      
      // Extract SLIITID from the QR data
      const SLIITID = qrData.SLIITID || scannedData;
      if (!SLIITID) {
        throw new Error('No registration number found in QR code');
      }
      
      // console.log('Verifying member with registration number:', SLIITID);
      
      // Use the QRMember.php endpoint
      const apiUrl = `https://dev.sliitleo.org/Backend/QRMember.php?SLIITID=${encodeURIComponent(SLIITID)}`;
      // console.log('API URL:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        },
        credentials: 'omit'
      });
      
      const responseText = await response.text();
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error('Failed to parse JSON response:', e);
        throw new Error('Invalid response from server');
      }
      
      // console.log('Parsed response data:', data);
      
      if (data.success) {
        // If member exists
        if (data.member) {
          // console.log('Member found:', data.member);
          // Store member data in state for UI
          setMemberData(data.member);
          
          // Mark attendance with the member data directly
          try {
            // Pass the member data directly to avoid state timing issues
            await markAttendance('present', data.member);
          } catch (err) {
            console.error('Error in markAttendance:', err);
            showToast(`Error: ${err.message}`, 'error');
            resetScanner();
          }
        } 
        // If member doesn't exist but we have QR data, create new member
        else if (qrData.name && qrData.SLIITID) {
          // console.log('Creating new member from QR data:', qrData);
          try {
            await createMemberFromQR(qrData);
            // After creating member, try marking attendance again
            await markAttendance('present', qrData);
          } catch (err) {
            console.error('Error creating member:', err);
            showToast(`Error creating member: ${err.message}`, 'error');
            resetScanner();
          }
        } else {
          throw new Error('Member not found and insufficient data to create new member');
        }
      } else {
        throw new Error(data.error || 'Error verifying member');
      }
    } catch (err) {
      //setError('Error verifying member data');
      showToast('Error verifying member data', 'error');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Create a new member from QR code data
  const createMemberFromQR = async (memberData) => {
    try {
      const apiUrl = 'http://localhost/portel/portalbackend/QRMember.php';
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(memberData),
        credentials: 'omit'
      });
      
      const data = await response.json();
      
      if (data.success) {
        // console.log('Member created/updated successfully');
        // Retry the member lookup
        await handleScan(JSON.stringify(memberData));
      } else {
        throw new Error(data.error || 'Failed to create/update member');
      }
    } catch (err) {
      console.error('Error creating/updating member:', err);
      showToast(err.message || 'Error processing member data', 'error');
    }
  };

  const resetScanner = () => {
    stopCamera();
    setResult(null);
    setMemberData(null);
    setFilePreview(null);
    setUseFileUpload(false);
    setIsScanning(true);
    
    // Restart the camera after a short delay
    setTimeout(() => {
      if (videoRef.current && !useFileUpload) {
        startCamera();
      }
    }, 100);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check if file is an image
    if (!file.type.match('image.*')) {
      showToast('Please upload an image file', 'error');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setFilePreview(e.target.result);
      processImageForQR(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const processImageForQR = async (imageSrc) => {
    setLoading(true);
    //setError(null);
    
    try {
      // Check if BarcodeDetector is available
      if (!window.BarcodeDetector) {
        throw new Error('QR code scanning is not supported in this browser');
      }
      
      const barcodeDetector = new BarcodeDetector({
        formats: ['qr_code']
      });
      
      const img = new Image();
      
      // Create a promise to handle the image loading
      const imageLoaded = new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = imageSrc;
      });
      
      await imageLoaded;
      
      // Create a canvas with optimal size for detection
      const canvas = document.createElement('canvas');
      const MAX_SIZE = 1000; // Maximum dimension for the canvas
      let width = img.width;
      let height = img.height;
      
      // Resize if image is too large
      if (width > MAX_SIZE || height > MAX_SIZE) {
        if (width > height) {
          height = Math.round((height * MAX_SIZE) / width);
          width = MAX_SIZE;
        } else {
          width = Math.round((width * MAX_SIZE) / height);
          height = MAX_SIZE;
        }
      }
      
      canvas.width = width;
      canvas.height = height;
      
      const context = canvas.getContext('2d', { willReadFrequently: true });
      
      // Draw image on canvas with smoothing
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      context.drawImage(img, 0, 0, width, height);
      
      // Multiple detection strategies
      const detectionAttempts = [
        // 1. Try jsQR first (most reliable for standard QR codes)
        async () => {
          const imageData = context.getImageData(0, 0, width, height);
          const code = jsQR(
            imageData.data,
            imageData.width,
            imageData.height,
            {
              inversionAttempts: 'dontInvert',
            }
          );
          
          if (code) {
            return [{ rawValue: code.data }];
          }
          return [];
        },
        // 2. Original image with BarcodeDetector
        async () => {
          if (BarcodeDetector) {
            return await barcodeDetector.detect(canvas);
          }
          return [];
        },
        // 3. High contrast black and white with jsQR
        async () => {
          const imageData = context.getImageData(0, 0, width, height);
          const data = imageData.data;
          
          // Convert to grayscale
          for (let i = 0; i < data.length; i += 4) {
            const gray = Math.min(255, Math.max(0, 
              data[i] * 0.3 + data[i + 1] * 0.59 + data[i + 2] * 0.11
            ));
            
            // Apply threshold
            const threshold = 128;
            const value = gray > threshold ? 255 : 0;
            
            data[i] = data[i + 1] = data[i + 2] = value;
          }
          
          context.putImageData(imageData, 0, 0);
          
          // Try jsQR with the processed image
          const code = jsQR(
            imageData.data,
            imageData.width,
            imageData.height,
            {
              inversionAttempts: 'attemptBoth',
            }
          );
          
          if (code) {
            return [{ rawValue: code.data }];
          }
          
          // If jsQR failed, try BarcodeDetector
          if (BarcodeDetector) {
            return await barcodeDetector.detect(canvas);
          }
          
          return [];
        },
        // 4. Inverted colors with jsQR
        async () => {
          const imageData = context.getImageData(0, 0, width, height);
          const data = imageData.data;
          
          // Invert colors
          for (let i = 0; i < data.length; i += 4) {
            data[i] = 255 - data[i];
            data[i + 1] = 255 - data[i + 1];
            data[i + 2] = 255 - data[i + 2];
          }
          
          context.putImageData(imageData, 0, 0);
          
          // Try jsQR with inverted colors
          const code = jsQR(
            imageData.data,
            imageData.width,
            imageData.height,
            {
              inversionAttempts: 'attemptBoth',
            }
          );
          
          if (code) {
            return [{ rawValue: code.data }];
          }
          
          // If jsQR failed, try BarcodeDetector
          if (BarcodeDetector) {
            return await barcodeDetector.detect(canvas);
          }
          
          return [];
        }
      ];
      
      // Try each detection method
      let barcodes = [];
      for (const attempt of detectionAttempts) {
        try {
          const result = await attempt();
          if (result && result.length > 0) {
            barcodes = result;
            break;
          }
        } catch (err) {
          console.warn('Detection attempt failed:', err);
        }
      }
      
      if (barcodes.length > 0) {
        const qrData = barcodes[0].rawValue;
        if (qrData) {
          handleScan(qrData);
          return;
        }
      }
      
      // If we get here, no QR code was found
      showToast('No QR code found in the image. Make sure the QR code is clear and well-lit.', 'error');
      
    } catch (err) {
      console.error('Error processing QR code:', err);
      showToast(
        err.message.includes('unsupported') 
          ? 'QR code scanning is not supported in this browser. Please try a different browser like Chrome or Edge.'
          : `Error: ${err.message || 'Failed to process image'}`
        , 'error');
    } finally {
      setLoading(false);
    }

  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const markAttendance = async (status = 'present', memberDataToUse = null) => {
    try {
      setLoading(true);
      
      // Get the current event ID from the URL or props
      const urlParams = new URLSearchParams(window.location.search);
      let eventId = urlParams.get('eventId');
      
      // If no eventId in URL, check localStorage for last used event
      if (!eventId) {
        eventId = localStorage.getItem('lastEventId');
      }
      
      if (!eventId) {
        // If still no eventId, show error and redirect
        showToast('No event selected. Please go back and select an event.', 'error');
        // Store the member data to process after event selection
        if (memberData) {
          localStorage.setItem('pendingAttendance', JSON.stringify({
            memberData: memberDataToUse || memberData,
            status
          }));
        }
        // Redirect to events page after a short delay
        setTimeout(() => {
          window.location.href = '/events';
        }, 2000);
        return;
      }
      
      // Get the member ID - always use SLIITID as the key
      let memberId = null;
      let memberName = '';
      
      // Use the provided memberData or fall back to component state
      const currentMemberData = memberDataToUse || memberData || (result && typeof result === 'object' ? result : null);
      
      if (currentMemberData) {
        // Extract SLIITID from various possible locations
        const sliitId = currentMemberData.SLIITID || 
                       (currentMemberData.member && currentMemberData.member.SLIITID) ||
                       currentMemberData.id || 
                       (currentMemberData.member && currentMemberData.member.id);
        
        // Ensure we have a valid SLIITID
        if (!sliitId) {
          throw new Error('No valid SLIITID found in member data');
        }
        
        // Use only the SLIITID without prefix
        memberId = sliitId.toString().replace(/^SLIITID:/, '');
        
        // Get member name if available
        memberName = currentMemberData.name || 
                    currentMemberData.FullName || 
                    (currentMemberData.member && currentMemberData.member.name) ||
                    'Unknown Member';
      }
      
      // If still no memberId, try to parse result
      if (!memberId && result) {
        try {
          const parsedResult = typeof result === 'string' ? JSON.parse(result) : result;
          if (parsedResult?.id) {
            memberId = parsedResult.id;
          } else if (parsedResult?.SLIITID) {
            memberId = `SLIITID:${parsedResult.SLIITID}`;
          } else if (typeof parsedResult === 'string' && parsedResult.trim() !== '') {
            // If it's just a plain string, assume it's a SLIITID
            memberId = `SLIITID:${parsedResult.trim()}`;
          }
        } catch (e) {
          // console.log('Result is not a JSON string, using as is');
          if (result && typeof result === 'string' && result.trim() !== '') {
            memberId = `SLIITID:${result.trim()}`;
          }
        }
      }
      
      if (!memberId) {
        console.error('No valid member ID found for attendance');
        // console.log('Available data:', { memberData, result });
        throw new Error('No member ID found for attendance');
      }
      
      // First, check if attendance already exists
      const checkUrl = `http://localhost/portel/portalbackend/check_attendance.php?member_id=${encodeURIComponent(memberId)}&event_id=${encodeURIComponent(eventId)}`;
      
      const checkResponse = await fetch(checkUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        },
        credentials: 'include'  // Important for session/cookie handling
      });
      
      let checkData;
      try {
        const responseText = await checkResponse.text();
        try {
          checkData = responseText ? JSON.parse(responseText) : {};
        } catch (e) {
          console.error('Error parsing check response as JSON:', e);
          // console.log('Raw check response:', responseText);
          throw new Error('Invalid response format from server');
        }
      } catch (err) {
        console.error('Error checking attendance:', err);
        throw new Error('Failed to check attendance status');
      }
      
      if (checkData.exists) {
        // Show success toast and reset
        showToast('Member already marked as present for this event.', 'success');
        setTimeout(() => {
          resetScanner();
        }, 2000);
        return;
      }
      // Get logged-in user's info from localStorage
      const user = JSON.parse(localStorage.getItem('user')) || {
        name: 'System',
        SLIITID: 'SYSTEM',
        role: 'System',
        ContactNo: '',
        avatar: null
      };
      
      // If no existing attendance, mark as present
      const apiUrl = 'https://dev.sliitleo.org/Backend/attendance.php';
      
      // Use the logged-in user's SLIITID as checked_by
      const checkedBy = user.SLIITID || 'SYSTEM';
      
      const requestData = {
        member_id: memberId,
        event_id: eventId,
        status: status,
        notes: 'Scanned via QR code',
        checked_by: checkedBy,
        timestamp: new Date().toISOString()
      };
      
      // console.log('Marking attendance with data:', requestData);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestData),
        credentials: 'include'  // Important for session/cookie handling
      });

      let responseData;
      try {
        const responseText = await response.text();
        try {
          responseData = responseText ? JSON.parse(responseText) : {};
        } catch (e) {
          console.error('Error parsing response as JSON:', e);
          // console.log('Raw response:', responseText);
          throw new Error('Invalid response format from server');
        }
      } catch (e) {
        console.error('Error parsing response:', e);
        throw new Error('Failed to process server response');
      }

      if (!response.ok) {
        const errorMessage = responseData.error || response.statusText || 'Unknown error';
        console.error('Server error:', errorMessage);
        throw new Error(`Server error: ${errorMessage}`);
      }
      
      // console.log('Attendance marked successfully:', responseData);
      showToast('Attendance marked successfully!', 'success');
      
      // Reset the scanner after a short delay
      setTimeout(() => {
        resetScanner();
      }, 2000);
      
      if (onScanComplete) {
        onScanComplete(responseData);
      }
    } catch (err) {
      console.error('Error marking attendance:', err);
      showToast(`Failed to mark attendance: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  if (disabled) {
    return (
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <FaTimesCircle className="h-5 w-5 text-yellow-400" aria-hidden="true" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-700">
              Please select an event to start scanning attendance.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center p-4 max-w-md mx-auto">
      <h2 className="text-2xl font-bold mb-4 text-center">Scan Member QR Code</h2>
      
      {isScanning ? (
  <div className="w-full max-w-md">
        {/* Camera selector */}
        {availableCameras.length > 1 && isScanning && !useFileUpload && (
          <div className="mb-4">
            <label htmlFor="camera-select" className="block text-sm font-medium text-gray-700 mb-1">
              Select Camera
            </label>
            <select
              id="camera-select"
              value={activeCamera?.deviceId || ''}
              onChange={(e) => switchCamera(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            >
              {availableCameras.map((camera) => (
                <option key={camera.deviceId} value={camera.deviceId}>
                  {camera.label || `Camera ${availableCameras.indexOf(camera) + 1}`}
                </option>
              ))}
            </select>
          </div>
        )}
        
        <div className="relative w-full" style={{ paddingBottom: '100%' }}>
          {useFileUpload ? (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              {filePreview ? (
                <div className="mb-4">
                  <img 
                    src={filePreview} 
                    alt="QR Code Preview" 
                    className="max-h-64 mx-auto rounded-lg"
                  />
                </div>
              ) : (
                <div className="py-8">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
                    <FaQrcode className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Upload QR Code</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Drag and drop an image containing a QR code, or click to browse
                  </p>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
              />
              <button
                type="button"
                onClick={triggerFileInput}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {filePreview ? 'Try Another Image' : 'Select Image'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setUseFileUpload(false);
                  setFilePreview(null);
                }}
                className="ml-3 inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Use Camera
              </button>
            </div>
          ) : (
            <div className="border-2 border-dashed border-gray-300 rounded-lg overflow-hidden relative" style={{ height: '50vh' }}>
              <div className="relative w-full h-full overflow-hidden">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />
                {/* Add a semi-transparent overlay with a square guide */}
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                  <div className="relative w-4/5 max-w-md aspect-square border-4 border-white border-opacity-70 rounded-lg">
                    <div className="absolute -inset-1 border-2 border-white border-dashed opacity-50"></div>
                  </div>
                </div>
              </div>
              <canvas ref={canvasRef} className="hidden" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="border-4 border-white border-dashed rounded-lg w-64 h-64 flex items-center justify-center">
                  <FaCamera className="text-white text-4xl opacity-50" />
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-2 bg-black bg-opacity-50">
                <button
                  type="button"
                  onClick={() => setUseFileUpload(true)}
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <FaQrcode className="mr-2" />
                  Upload QR Code Image Instead
                </button>
              </div>
            </div>
          )}
        </div>
        </div>
      ) : (
        <div className="w-full max-w-md border-1 border-gray-200 rounded-lg p-4 bg-gray-50">
          {loading ? (
            <div className="flex flex-col items-center justify-center p-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
              <p>Processing...</p>
            </div>
          ) : memberData ? (
            <div className="text-center">
              <div className="text-green-500 text-6xl mb-4">
                <FaUserCheck className="mx-auto" />
              </div>
              <h3 className="text-xl text-gray-800 font-semibold">{memberData.name}</h3>
              <p className="text-gray-600">{memberData.role}</p>
              <p className="text-sm text-gray-500 mt-2">SLIIT ID: {memberData.SLIITID}</p>
              
              <div className="mt-6 space-y-2">
                <button
                  onClick={() => markAttendance('present')}
                  disabled={loading}
                  className="w-full bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-md flex items-center justify-center gap-2"
                >
                  <FaCheckCircle /> Mark as Present
                </button>
                <button
                  onClick={resetScanner}
                  className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-4 rounded-md"
                >
                  Scan Another
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center p-4 bg-gray-800">
              <div className="text-red-500 text-6xl mb-4 text-white bg-gray-800">
                <FaTimesCircle className="mx-auto" />
              </div>
              <h3 className="text-xl font-semibold text-red-500">Member Not Found</h3>
              <p className="text-gray-300 mt-2">The scanned QR code is not associated with any active member.</p>
              <button
                onClick={resetScanner}
                className="mt-4 bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-md"
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      )}
      

    
      <div className="mt-6 text-sm text-gray-200 text-center">
        
        <p>
          {useFileUpload 
            ? 'Upload an image containing a QR code' 
            : 'Point the camera at a member\'s QR code to mark attendance'}
        </p>
        {!('BarcodeDetector' in window) && (
          <p className="text-yellow-600 mt-2">
            Note: Your browser has limited QR code scanning support. 
            For best results, use the latest version of Chrome, Edge, or upload a QR code image.
          </p>
        )}
      </div>
    </div>
  );
};

export default QRScanner;
